import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { NumberInput } from '@/components/ui/number-input';
import { NominateBallot } from './NominateBallot';
import { AllocateBallot } from './AllocateBallot';
import { RevealPanel } from './RevealPanel';
import { ShortlistEditor } from './ShortlistEditor';
import { ResultsTable } from './ResultsTable';
import { BallotStatus } from './BallotStatus';
import { PublicationTab } from './PublicationTab';
import { computePhase } from '@/lib/selection-phase';
import {
  createRound,
  transitionRound,
  revealAllocateRound,
  type CandidateHappening,
} from '@/lib/selections-api';
import type {
  Selection,
  SelectionRound,
  SelectionVoter,
  NominateRoundConfig,
  AllocateRoundConfig,
} from '@littlebooker/dal';

interface ControlRoomProps {
  selectionId: string;
  selection: Selection;
  rounds: SelectionRound[];
  candidates: CandidateHappening[];
  voters: SelectionVoter[];
  myRecusedIds: Set<string>;
  isOwner: boolean;
  isCurrentUserVoter: boolean;
  currentUserId: string | undefined;
  userName: (uid: string) => string;
  targetShortlist: number;
  targetFinal: number;
  suggestedMaxPicks: number;
  revealedShortlistIds: string[];
  onManage: () => void;
  onChanged: () => void;
}

const waiting = (msg: string) => <p className="text-sm text-muted-foreground">{msg}</p>;

export function ControlRoom(props: ControlRoomProps) {
  const {
    selectionId,
    selection,
    rounds,
    candidates,
    voters,
    myRecusedIds,
    isOwner,
    isCurrentUserVoter,
    currentUserId,
    userName,
    targetShortlist,
    targetFinal,
    suggestedMaxPicks,
    revealedShortlistIds,
    onManage,
    onChanged,
  } = props;

  const phase = computePhase(rounds);

  const close = useMutation({
    mutationFn: (roundId: string) => transitionRound(selectionId, roundId, 'closed'),
    onSuccess: onChanged,
  });
  const revealAllocate = useMutation({
    mutationFn: (roundId: string) => revealAllocateRound(selectionId, roundId),
    onSuccess: onChanged,
  });

  const nominateBallot = (round: SelectionRound) =>
    isCurrentUserVoter ? (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Cast your nominations</CardTitle>
          <CardDescription>Private until the round is revealed.</CardDescription>
        </CardHeader>
        <CardContent>
          <NominateBallot
            selectionId={selectionId}
            roundId={round.id}
            candidates={candidates}
            recusedIds={myRecusedIds}
            config={round.config as NominateRoundConfig}
            editable
          />
        </CardContent>
      </Card>
    ) : null;

  const allocateBallot = (round: SelectionRound) => {
    if (!isCurrentUserVoter) return null;
    const own = (round.config as { shortlist_happening_ids?: string[] }).shortlist_happening_ids ?? [];
    const effective = own.length > 0 ? own : revealedShortlistIds;
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Spend your points</CardTitle>
          <CardDescription>Private until the round is revealed.</CardDescription>
        </CardHeader>
        <CardContent>
          <AllocateBallot
            selectionId={selectionId}
            roundId={round.id}
            candidates={candidates}
            recusedIds={myRecusedIds}
            config={round.config as AllocateRoundConfig}
            shortlistIds={effective}
            editable
          />
        </CardContent>
      </Card>
    );
  };

  const ownerRoundMgmt = (round: SelectionRound, label: string) =>
    isOwner ? (
      <Card>
        <CardHeader className="pb-2">
          <CardDescription>Owner</CardDescription>
          <CardTitle className="text-base">{label}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button size="sm" variant="outline" onClick={() => close.mutate(round.id)} disabled={close.isPending}>
            {close.isPending ? 'Closing…' : 'Close the round'}
          </Button>
          <BallotStatus selectionId={selectionId} round={round} voters={voters} userName={userName} />
        </CardContent>
      </Card>
    ) : null;

  // ---- render by phase ----
  let panel: React.ReactNode = null;

  if (phase.key === 'setup') {
    panel = isOwner ? (
      <StartRoundCard
        kind="nominate"
        selectionId={selectionId}
        suggestedMaxPicks={suggestedMaxPicks}
        candidateCount={candidates.length}
        voterCount={voters.length}
        onManage={onManage}
        onChanged={onChanged}
      />
    ) : (
      waiting('The vote is being set up — you’ll be able to nominate soon.')
    );
  } else if (phase.key === 'nominate_open' && phase.nominate) {
    panel = (
      <>
        {nominateBallot(phase.nominate)}
        {ownerRoundMgmt(phase.nominate, 'Round 1 is open')}
        {!isCurrentUserVoter && !isOwner && waiting('Round 1 is open.')}
      </>
    );
  } else if (phase.key === 'nominate_closed' && phase.nominate) {
    panel = isOwner ? (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Round 1 closed — choose the shortlist</CardTitle>
        </CardHeader>
        <CardContent>
          <RevealPanel
            selectionId={selectionId}
            roundId={phase.nominate.id}
            targetShortlist={targetShortlist}
            targetFinal={targetFinal}
            expectedVoters={voters.length}
            mode="reveal"
            onRevealed={onChanged}
          />
        </CardContent>
      </Card>
    ) : (
      waiting('Ballots are in — waiting for the shortlist to be revealed.')
    );
  } else if (phase.key === 'shortlist' && phase.nominate) {
    const nominateRound = phase.nominate;
    const shortlistIds =
      (nominateRound.config as { shortlist_happening_ids?: string[] }).shortlist_happening_ids ?? [];
    panel = isOwner ? (
      <>
        <ShortlistEditor
          selectionId={selectionId}
          roundId={nominateRound.id}
          config={nominateRound.config as Record<string, unknown>}
          candidates={candidates}
          targetShortlist={targetShortlist}
          onChanged={onChanged}
        />
        <StartRoundCard
          kind="allocate"
          selectionId={selectionId}
          suggestedMaxPicks={suggestedMaxPicks}
          candidateCount={candidates.length}
          voterCount={voters.length}
          onManage={onManage}
          onChanged={onChanged}
        />
        <details className="rounded-md border p-3">
          <summary className="cursor-pointer text-sm font-medium">Re-cut by a different rule</summary>
          <div className="mt-2">
            <RevealPanel
              selectionId={selectionId}
              roundId={nominateRound.id}
              targetShortlist={targetShortlist}
              targetFinal={targetFinal}
              expectedVoters={voters.length}
              mode="recut"
              onRevealed={onChanged}
            />
          </div>
        </details>
      </>
    ) : (
      <>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Shortlist ({shortlistIds.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="list-inside list-disc text-sm text-muted-foreground">
              {shortlistIds.map((hid) => (
                <li key={hid}>{candidates.find((c) => c.id === hid)?.title ?? hid}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
        {waiting('The shortlist is set — round 2 (points) will open soon.')}
      </>
    );
  } else if (phase.key === 'allocate_open' && phase.allocate) {
    panel = (
      <>
        {allocateBallot(phase.allocate)}
        {ownerRoundMgmt(phase.allocate, 'Round 2 is open')}
        {!isCurrentUserVoter && !isOwner && waiting('Round 2 is open.')}
      </>
    );
  } else if (phase.key === 'allocate_closed' && phase.allocate) {
    const round = phase.allocate;
    panel = isOwner ? (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Round 2 closed — reveal the results</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-destructive">Revealing discloses all ballots and can’t be undone.</p>
          <Button size="sm" onClick={() => revealAllocate.mutate(round.id)} disabled={revealAllocate.isPending}>
            {revealAllocate.isPending ? 'Revealing…' : 'Reveal the results'}
          </Button>
        </CardContent>
      </Card>
    ) : (
      waiting('Ballots are in — waiting for the results to be revealed.')
    );
  } else if (phase.key === 'results' && phase.allocate) {
    panel = (
      <>
        <ResultsTable
          selectionId={selectionId}
          roundId={phase.allocate.id}
          candidates={candidates}
          targetFinal={targetFinal}
        />
        <PublicationTab
          selectionId={selectionId}
          selection={selection}
          rounds={rounds}
          candidates={candidates}
          voters={voters}
          isOwner={isOwner}
          isCurrentUserVoter={isCurrentUserVoter}
          currentUserId={currentUserId}
          userName={userName}
          targetFinal={targetFinal}
          onChanged={onChanged}
        />
      </>
    );
  }

  return <div className="space-y-4">{panel}</div>;
}

/** Owner card to configure + open the next round in one step. */
function StartRoundCard({
  kind,
  selectionId,
  suggestedMaxPicks,
  candidateCount,
  voterCount,
  onManage,
  onChanged,
}: {
  kind: 'nominate' | 'allocate';
  selectionId: string;
  suggestedMaxPicks: number;
  candidateCount: number;
  voterCount: number;
  onManage: () => void;
  onChanged: () => void;
}) {
  const [maxPicks, setMaxPicks] = useState(suggestedMaxPicks || 5);
  const [wildcards, setWildcards] = useState(2);
  const [requireNote, setRequireNote] = useState(false);
  const [budget, setBudget] = useState(100);
  const [perItemCap, setPerItemCap] = useState(30);
  const [error, setError] = useState('');

  const start = useMutation({
    mutationFn: async () => {
      const config =
        kind === 'nominate'
          ? { max_picks: maxPicks, wildcards, require_note: requireNote }
          : { budget, per_item_cap: perItemCap };
      const round = await createRound(selectionId, { kind, config });
      await transitionRound(selectionId, round.id, 'open');
    },
    onSuccess: onChanged,
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Failed to start the round'),
  });

  const numCls = 'w-20 rounded-md border border-input bg-background px-2 py-1';

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">
          {kind === 'nominate' ? 'Ready to start Round 1 (nominate)' : 'Start Round 2 (allocate)'}
        </CardTitle>
        <CardDescription>
          {candidateCount} candidates · {voterCount} voters
          {voterCount === 0 && ' — add voters before opening'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {kind === 'nominate' ? (
          <div className="flex flex-wrap items-end gap-4">
            <label className="text-sm">
              Max picks / voter
              <NumberInput min={1} allowEmpty={false} className={`${numCls} mt-1 block`} value={maxPicks} onChange={(n) => setMaxPicks(n ?? 1)} />
            </label>
            <label className="text-sm">
              Wildcards
              <NumberInput min={0} allowEmpty={false} className={`${numCls} mt-1 block`} value={wildcards} onChange={(n) => setWildcards(n ?? 0)} />
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={requireNote} onChange={(e) => setRequireNote(e.target.checked)} />
              Require a note
            </label>
          </div>
        ) : (
          <div className="flex flex-wrap items-end gap-4">
            <label className="text-sm">
              Budget
              <NumberInput min={1} allowEmpty={false} className={`${numCls} mt-1 block`} value={budget} onChange={(n) => setBudget(n ?? 1)} />
            </label>
            <label className="text-sm">
              Per-item cap
              <NumberInput min={1} allowEmpty={false} className={`${numCls} mt-1 block`} value={perItemCap} onChange={(n) => setPerItemCap(n ?? 1)} />
            </label>
          </div>
        )}
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex gap-2">
          <Button size="sm" onClick={() => start.mutate()} disabled={start.isPending || voterCount === 0}>
            {start.isPending ? 'Opening…' : kind === 'nominate' ? 'Open Round 1' : 'Open Round 2'}
          </Button>
          {kind === 'nominate' && (
            <Button size="sm" variant="outline" onClick={onManage}>
              Edit setup (scope / voters / recusals)
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
